// Selector de tipos de servicio con búsqueda remota (nombre). Debounce de
// 350ms sobre el texto; el dropdown indica si el tipo genera cobro.
import { useState } from 'react';
import { Select, Typography } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import { useT } from '../locales';
import { useTiposServicioList } from '../features/catalogos/api/useCatalogos';
import type { TipoServicioResponse } from '@aid/shared';
import { useDebouncedValue } from '../lib/useDebouncedValue';

const { Text } = Typography;

const DEBOUNCE_MS = 350;

interface TipoServicioOption extends DefaultOptionType {
  value: number;
  label: string;
  /** Línea secundaria del dropdown: "genera cobro" / "sin cobro". */
  secondary?: string;
}

interface TipoServicioSelectProps {
  value?: number | null;
  onChange?: (id: number | undefined) => void;
  disabled?: boolean;
  placeholder?: string;
}

function tipoServicioOption(
  t: TipoServicioResponse,
  tr: (key: string) => string,
): TipoServicioOption {
  const nombre = t.nombre ?? `#${t.id}`;
  return {
    value: t.id,
    label: nombre,
    secondary: t.generaCobro ? tr('common.generaCobro') : tr('common.sinCobro'),
    title: nombre,
  };
}

export function TipoServicioSelect({
  value,
  onChange,
  disabled,
  placeholder,
}: TipoServicioSelectProps) {
  const t = useT();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, DEBOUNCE_MS);

  const { data, isLoading, error } = useTiposServicioList(debouncedSearch || undefined);

  const tipos = data ?? [];
  const enLista = value != null && tipos.some((ts) => ts.id === value);
  const options: TipoServicioOption[] = [
    ...tipos.map((ts) => tipoServicioOption(ts, t)),
    ...(value != null && !enLista ? [{ value, label: `#${value}` }] : []),
  ];

  return (
    <Select
      style={{ width: '100%' }}
      showSearch={{ filterOption: false }}
      loading={isLoading}
      disabled={disabled}
      allowClear
      placeholder={placeholder ?? t('common.buscarTipoServicio')}
      defaultActiveFirstOption={false}
      notFoundContent={
        isLoading ? t('common.loading') : error ? t('common.error') : t('common.noResults')
      }
      value={value}
      onChange={onChange}
      onSearch={setSearch}
      options={options}
      optionRender={(option) => {
        const data = option.data as TipoServicioOption;
        return (
          <div>
            <div>{data.label}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {data.secondary}
            </Text>
          </div>
        );
      }}
    />
  );
}
